import styled from "styled-components";
import { mainTabs } from "../../../../../config/testIds";
import {
  ReadOnlyPractice,
  RequestMessage,
  SecondaryPracticeRequest,
  useCloseRequestMutation,
  useConfirmRequestClosureCheckMutation,
  useCreatePracticeRequestMutation,
  useForwardRequestMutation,
  useLazyGetAllPracticeRequestsQuery,
  useLazyGetPracticeRequestQuery,
  useReplyToRequestMessageMutation,
} from "../../../../../redux/apiSpecifications/apiCrud";
import { ContentWrapper } from "../../Commons/Wrappers";
import {
  RefreshInTabWrapper,
  WidgetWrapper,
} from "../../../../../style/DashbordWidgetWrappers";
import ButtonNewRequest from "./ButtonNewRequest";
import { useEffect, useMemo, useState } from "react";
import NewRequestModal from "./NewRequestModal/NewRequestModal";
import { useSelector } from "react-redux";
import { RootState } from "../../../../../redux/store";
import RequestsList, { RequestResultItem } from "./RequestsList";
import ManageRequestModal from "./ManagePracticeRequestModal/ManagePracticeRequestModal";
import { FetchBaseQueryError } from "@reduxjs/toolkit/query";
import { SerializedError } from "@reduxjs/toolkit";
import Loading from "../../../../Loading";
import { ApplicationErrorAlert } from "../../../../Layout/ApplicationErrorsModal";
import { useApplicationError } from "../../../../../hooks/useApplicationError";

const ListActions = styled(WidgetWrapper)`
  display: block;
  align-items: flex-start;
  max-width: 100%;
  overflow: auto;
  padding: 2em;

  .MuiPaper-root {
    box-shadow: none;
    width: 100%;
  }

  .MuiToolbar-root > .MuiBox-root {
    justify-content: flex-start;
  }

  .MuiTableCell-root {
    padding: 0;
  }
`;

const RequestsListWrapper = styled.div`
  margin-top: 16px;
`;

interface IAppraisalDetailsTabRequestsProps {
  practice: ReadOnlyPractice;
  isActive?: boolean;
}

const testIds = mainTabs.tabs.activities;

const AppraisalDetailsTabRequests = ({
  practice,
  isActive,
}: IAppraisalDetailsTabRequestsProps) => {
  const { showApplicationError } = useApplicationError();

  const [newRequestKey, setNewRequestKey] = useState<string | null>(null);

  const [selectedRequest, setSelectedRequest] =
    useState<SecondaryPracticeRequest | null>(null);

  const [getAllPracticeRequests, resultGetAllPracticeRequests] =
    useLazyGetAllPracticeRequestsQuery();

  const [getPracticeRequest, resultGetPracticeRequest] =
    useLazyGetPracticeRequestQuery();

  const [createPracticeRequest] = useCreatePracticeRequestMutation();
  const [closeRequest] = useCloseRequestMutation();
  const [replyToRequestMessage] = useReplyToRequestMessageMutation();
  const [forwardRequest] = useForwardRequestMutation();
  const [confirmRequestClosureCheck] = useConfirmRequestClosureCheckMutation();

  const { authorization, activeRole } = useSelector(
    (state: RootState) => state.user
  );

  useEffect(() => {
    if (!practice?.id) return;

    loadData();
  }, [practice?.appraiser, isActive]);

  const loadData = async () => {
    await getAllPracticeRequests({
      authorization: authorization,
      activeRole: activeRole!,
      id: practice?.id as number,
    });
  };

  const handleOnMenuClick = (key: string) => {
    setNewRequestKey(key);
  };

  const handleOnSendNewRequest = async (
    secondaryPracticeRequest: SecondaryPracticeRequest
  ) => {
    setNewRequestKey(null);

    const response = await createPracticeRequest({
      authorization: authorization,
      activeRole: activeRole!,
      id: practice?.id!,
      secondaryPracticeRequest: secondaryPracticeRequest,
    });

    const errorResponse = response as {
      error: FetchBaseQueryError | SerializedError;
    };

    if (errorResponse.error) {
      showApplicationError(errorResponse.error);
    } else {
      await loadData();
    }
  };

  const handleOnCanceldNewRequest = () => {
    setNewRequestKey(null);
  };

  const handleManageRequest = async (request: RequestResultItem) => {
    const { data, error, isSuccess } = await getPracticeRequest({
      activeRole: activeRole!,
      authorization: authorization,
      id: request?.id as number,
    });

    if (isSuccess && data) {
      setSelectedRequest(data);
    } else {
      showApplicationError(error!);
    }
  };

  const showRefresh = useMemo(() => {
    return (
      ["pending"].includes(resultGetAllPracticeRequests.status) ||
      ["pending"].includes(resultGetPracticeRequest.status)
    );
  }, [resultGetAllPracticeRequests, resultGetPracticeRequest]);

  const handleOnCancelManageRequest = () => setSelectedRequest(null);

  const handleOnConfirmCloseRequest = async (
    requestMessage?: RequestMessage
  ) => {
    setSelectedRequest(null);

    const response = await closeRequest({
      authorization: authorization,
      activeRole: activeRole!,
      id: selectedRequest?.id!,
      requestMessage: requestMessage ?? ({} as RequestMessage),
    });

    const errorResponse = response as {
      error: FetchBaseQueryError | SerializedError;
    };

    if (errorResponse.error) {
      showApplicationError(errorResponse.error);
    } else {
      await loadData();
    }
  };

  const handleOnConfirmForwardMessage = async (
    requestMessage: RequestMessage
  ) => {
    setSelectedRequest(null);

    const response = await forwardRequest({
      authorization: authorization,
      activeRole: activeRole!,
      id: selectedRequest?.id!,
      requestMessage: requestMessage,
    });

    const errorResponse = response as {
      error: FetchBaseQueryError | SerializedError;
    };

    if (errorResponse.error) {
      showApplicationError(errorResponse.error);
    } else {
      await loadData();
    }
  };

  const handleOnConfirmReplyMessage = async (
    requestMessage: RequestMessage
  ) => {
    setSelectedRequest(null);

    const response = await replyToRequestMessage({
      authorization: authorization,
      activeRole: activeRole!,
      id: selectedRequest?.id!,
      requestMessage: requestMessage,
    });

    const errorResponse = response as {
      error: FetchBaseQueryError | SerializedError;
    };

    if (errorResponse.error) {
      showApplicationError(errorResponse.error);
    } else {
      await loadData();
    }
  };

  const handleOnConfirmHaveReadMessage = async () => {
    setSelectedRequest(null);

    const response = await confirmRequestClosureCheck({
      authorization: authorization,
      activeRole: activeRole!,
      id: selectedRequest?.id!,
    });

    const errorResponse = response as {
      error: FetchBaseQueryError | SerializedError;
    };

    if (errorResponse.error) {
      showApplicationError(errorResponse.error);
    } else {
      await loadData();
    }
  };

  return (
    <>
      {showRefresh && (
        <RefreshInTabWrapper>
          <Loading />
        </RefreshInTabWrapper>
      )}

      <ContentWrapper data-testid={testIds.container}>
        <ListActions>
          <div>
            <ButtonNewRequest
              practice={practice}
              onMenuClick={handleOnMenuClick}
            />
          </div>

          <RequestsListWrapper>
            {resultGetAllPracticeRequests.status === "rejected" && (
              <ApplicationErrorAlert
                error={resultGetAllPracticeRequests.error!}
              />
            )}

            {resultGetAllPracticeRequests.status === "fulfilled" && (
              <RequestsList
                items={resultGetAllPracticeRequests.data}
                onSelectItem={handleManageRequest}
              />
            )}
          </RequestsListWrapper>
        </ListActions>
      </ContentWrapper>

      {newRequestKey && (
        <NewRequestModal
          category={newRequestKey}
          onSend={handleOnSendNewRequest}
          onCancel={handleOnCanceldNewRequest}
        />
      )}

      {selectedRequest && (
        <ManageRequestModal
          practiceRequest={selectedRequest}
          onCancel={handleOnCancelManageRequest}
          onConfirmCloseRequest={handleOnConfirmCloseRequest}
          onConfirmForwardMessage={handleOnConfirmForwardMessage}
          onConfirmReplyMessage={handleOnConfirmReplyMessage}
          onConfirmHaveReadMessage={handleOnConfirmHaveReadMessage}
        />
      )}
    </>
  );
};

export default AppraisalDetailsTabRequests;
